<template>
    <div class="z-index-2">
      <div class="p-3 pb-0">
        <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
            {{ dateRange }}
        </div>
        <h5>{{ title }}</h5>
      </div>
      <div class="p-3 pt-2">
        <div class="chart">
          <canvas :id="id" class="chart-canvas" :height="height"></canvas>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import ChartUtils from "@/helpers/chartUtils";
  import Chart from "chart.js/auto";
  import annotationPlugin from "chartjs-plugin-annotation";
  import styles from "./UserStressSpoChart.vue?vue&type=style&index=0&lang=scss&module=1";

  
  Chart.register(annotationPlugin);
  
  export default {
    name: "UserStressSpoChart",
    props: {
      id: {
        type: String,
        default: "user-strain-spo-chart",
      },
      title: {
        type: String,
        default: "Stress",
      },
      height: {
        type: String,
        default: "300",
      },
      chartData: {
        type: Object,
        required: true,
      },
      dateRange: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        chartInfo: {},
      };
    },
    watch: {
      chartData() {
        this.addChart();
      },
    },
    mounted() {
      Chart.defaults.borderColor = styles.darkGray;
      this.addChart();
    },
    methods: {
      addChart() {
        this.chartInfo = {
            labels: this.chartData.chartLabels,
            datasets:[
                {  
                    label:"SpO2(%)",
                    data: this.chartData.spoScores
                },
                {
                    label:"STRESS SCORE",
                    data: this.chartData.stressScores
                }
            ]
        };

        const ctx = document.getElementById(this.id).getContext("2d");
        let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
        gradientStroke1.addColorStop(1, "rgba(250,250,190,0.3)");
        gradientStroke1.addColorStop(0, "transparent");
  
        let chartStatus = Chart.getChart(this.id);
        if (chartStatus != undefined) {
          chartStatus.destroy();
        }
  
        // let tempArray = this.chartData.spoScores.filter(a => a > 0);
        // let maxSpo = Math.max(...tempArray);
        let tempArray = this.chartData.stressScores.filter(a => a > 0);
        let minStress = Math.min(...tempArray);
        let maxStress = Math.max(...tempArray);
        
        new Chart(ctx, {
          data: {
            labels: this.chartInfo.labels,
            datasets: [
              {
                type: "bar",
                label: 'SpO2',
                weight: 5,
                tension: 0.4,
                borderWidth: 0,
                pointBackgroundColor: styles.colorStress,
                borderColor: styles.colorStress,
                backgroundColor: styles.colorStress,
                borderRadius: 4,
                borderSkipped: false,
                data: this.chartInfo.datasets[0].data,
                maxBarThickness: 30,
                order: 2,
                yAxisID: "y1",
              },
              {
                type: "line",
                label: 'Stress',
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: styles.colorSpo2,
                borderColor: styles.colorSpo2,
                borderWidth: 3,
                backgroundColor: gradientStroke1,
                data: this.chartInfo.datasets[1].data,
                fill: true,
                order: 1,
                yAxisID: "y",
              }
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              annotation: {
                annotations: {
                  line1: {
                    type: "line",
                    borderColor: styles.colorSpo2,
                    borderDash: [6, 6],
                    borderDashOffset: 0,
                    borderWidth: 1,
                    label: {
                      display: true,
                      content: (ctx) =>
                        `Avg Stress: ${ChartUtils.average(ctx, 1).toFixed(1)}`,
                      position: "start",
                    },
                    scaleID: "y",
                    value: (ctx) => ChartUtils.average(ctx, 1),
                  },
                  line2: {
                    type: "line",
                    borderColor: styles.colorStress,
                    borderDash: [6, 6],
                    borderDashOffset: 0,
                    borderWidth: 1,
                    label: {
                      display: true,
                      content: (ctx) =>
                        `Avg SpO2: ${Math.round(ChartUtils.average(ctx, 0))}`,
                      position: "end",
                    },
                    scaleID: "y1",
                    value: (ctx) => ChartUtils.average(ctx, 0),
                  },
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  color: styles.colorAxes,
                  boxWidth: 12,
                  boxHeight: 12,
                  padding: 40,
                },
              },
            },
            interaction: {
              intersect: false,
              mode: "index",
            },
            scales: {
              y1: {
                min: 90,
                //max: maxSpo,
                max: 100,
                display: true,
                position: "right",
                ticks: {
                  stepSize: 1,
                  color: styles.gray,
                },
                title: {
                  display: true,
                  text: this.chartInfo.datasets[0].label,
                  color: styles.colorAxes,
                  font: {
                    family: "NotoSans-Regular",
                  },
                },
                // grid line settings
                grid: {
                  drawOnChartArea: false, // only want the grid lines for one axis to show up
                },
              },
              x: {
                grid: {
                  drawBorder: false,
                  display: true,
                  drawOnChartArea: true,
                  drawTicks: true,
                  borderDash: [5, 5],
                },
                ticks: {
                  display: true,
                  color: styles.gray,
                  padding: 10,
                  font: {
                    size: 12,
                    style: "normal",
                    lineHeight: 2,
                  },
                },
              },
              y: {
                min: minStress - 3,
                max: maxStress + 3,
                display: true,
                position: "left",
                ticks: {
                  stepSize: 1,
                  color: styles.gray,
                },
                title: {
                  display: true,
                  text: this.chartInfo.datasets[1].label,
                  color: styles.colorAxes,
                  font: {
                    family: "NotoSans-Regular",
                  },
                },
                // grid line settings
                grid: {
                  drawOnChartArea: true,
                },
              },
            },
          },
        });
      }
    },
  };
  </script>
  <style lang="scss" scoped>
  :export {
    darkRed: $g-red_900;
    red: $g-red_500;
    gray: $color-muted-text;
    darkGray: $color-gridlines;
    colorStress: $g-blue_500;
    colorSpo2: $g-yellow_500;
    colorAxes: $color-chart-axes-text;
    colorLimitline: $color-chart-limitline;
   }
   .date-range {
    color: #bccdcb;
    font-weight: 100;
    font-size: 0.75rem;
   }
  </style>
  